import React, { useMemo } from "react";
import moment, { Moment } from "moment";
import "moment-timezone";
import { Button } from "@amzn/stencil-react-components/button";

interface GenerateIcsParams {
  title: string;
  startTime: Moment | string;
  endTime?: Moment | string;
  location?: string;
}

// eslint-disable-next-line import/no-unused-modules
export function generateIcs({ title, startTime, endTime, location }: GenerateIcsParams): string {
  const momentFormatStr = "YYYYMMDDTHHmmss[Z]";

  const start = typeof startTime === "string" ? moment(new Date(startTime)) : startTime;
  const end = endTime ? (typeof endTime === "string" ? moment(new Date(endTime)) : endTime) : undefined;

  /// https://datatracker.ietf.org/doc/html/rfc5545
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `DTSTART:${start.utc().format(momentFormatStr)}`,
    `SUMMARY:${title}`,
    end && `DTEND:${end.utc().format(momentFormatStr)}`,
    location && `LOCATION:${location}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
}

interface AddToCalendarProps {
  children: React.ReactNode;
  filename?: string;
  event: {
    startTime: Moment | string;
    endTime?: Moment | string;
    location?: string;
    title: string;
  };
}

// eslint-disable-next-line import/no-unused-modules
export const AddToCalendar = ({
  children,
  event,
  filename,
  ...buttonProps
}: AddToCalendarProps & React.ComponentProps<typeof Button>) => {
  const download = filename ?? "download.ics";

  const contentType = "text/calendar;charset=utf8";
  const icsContent = generateIcs(event);
  const linkRef = useMemo(
    () => window.URL.createObjectURL(new Blob([icsContent], { type: contentType })),
    [icsContent]
  );

  return (
    <a data-testid="add-to-calendar-component" download={download} href={linkRef}>
      <Button {...buttonProps}>{children}</Button>
    </a>
  );
};
